import { cloneDeep, isNumber } from 'lodash';
import { v4 as uuid } from 'uuid';
import { Message } from '@meitu/saas-ui';
import { addAdvancedTextMaterialId, Framework, getTrackFunc, resetLayer } from '@/framework';
import { TableLayer } from '@/layers/table-layer';
import { getLastMousePoint } from '@/utils/event-kit';
import { getLayerInfoWithRotate, isRectOutOfBounds } from '@/utils/layer-kit';
import { asyncTraverseTrees, traverseTree } from '@/utils/tree-kit';
import {
    COPY_DISTANCE,
    IPosition,
    ISize,
    LayerType,
    MAX_STAGES,
    MAX_STAGES_TIP,
    type IExtraInfo,
    type ILayerProps,
    type IPoint,
} from '@/version-types';
import type { ClipboardPluginDependency } from './dependency';
import { readClipboardData, writeClipboardData } from './io';
import { IClipboardData, IClipboardHotkeyType, IClipboardLayer, IClipboardPage, IClipboardTemplate } from './types';
import { isUseSystemClipboard, removeAIPPTExtraInfo } from './utils';

function makeSurePointInStage(point: IPoint, layer: ILayerProps, stageSize: ISize) {
    const layerBounding = {
        left: point.x,
        top: point.y,
        width: layer.width,
        height: layer.height,
        rotate: layer.rotate,
    };

    const stageCenterPosition = {
        x: (stageSize.width - layer.width) / 2,
        y: (stageSize.height - layer.height) / 2,
    };

    // 如果粘贴的图层不在画布内，则调整到画布的中心
    const newLayerInfo = getLayerInfoWithRotate(layerBounding);

    // 计算图层是否在舞台中
    if (!isRectOutOfBounds(newLayerInfo, { ...stageSize, left: 0, top: 0 })) {
        return point;
    }

    return stageCenterPosition;
}

const FRAME_PASTE_GAP = 20;

export interface ClipboardPluginConfig {
    allowPasteLayerTypes: LayerType[];
    disablePasteStage: boolean;
}

export class ClipboardPlugin extends Framework {
    private pasteTextHandles = new Set<number>();

    private framePasteInfo: { nextLeft: number; top: number } | null = null;

    // 粘贴依赖的信息
    private pasteInfo: {
        originIndex: number;
        // 记录相对图层（单图层或者多选的临时组）的图层原始位置，用户获取粘贴的相对位置
        left: number;
        top: number;
        frame: IPosition;
        // 这个主要用来解决跨页面粘贴时，不同画布尺寸需要重新计算粘贴位置
        extraInfo: IExtraInfo;
        // 记录相对图层或者临时组内图层的原始位置和 id，用来判断移动和删除
        layers: Array<{
            id: string;
            left: number;
            top: number;
        }>;
    } | null = null;

    public config: ClipboardPluginConfig = {
        allowPasteLayerTypes: [],
        disablePasteStage: false,
    };

    constructor(identity: string) {
        super(identity);
    }

    getClipboardData(e: ClipboardEvent | null) {
        return readClipboardData(e);
    }

    private setPasteInfo({ layer, extraInfo }: IClipboardLayer, explicitOriginIndex?: number) {
        const originIndex = explicitOriginIndex ?? this.framework.service.frame.getLayerFrameId(layer.id);
        if (!isNumber(originIndex)) return;

        this.pasteInfo = {
            originIndex,
            left: layer.left,
            top: layer.top,
            frame: {
                left: this.framework.service.frame.getShape(originIndex).left,
                top: this.framework.service.frame.getShape(originIndex).top,
            },
            extraInfo,
            // ATTENTION: 多选的临时组需要记录所有的一级子图层，用来判断是否移动或者删除，因为临时组在取消多选时会丢失
            layers: this.framework.service.flatLayer.checkIsExtraGroup(layer)
                ? layer.children.map((child) => ({
                      id: child.id,
                      left: child.left,
                      top: child.top,
                  }))
                : [
                      {
                          id: layer.id,
                          left: layer.left,
                          top: layer.top,
                      },
                  ],
        };
    }

    /**
     * 设置拷贝信息
     * 原来叫做 setCopyInfo
     * @param template - 拷贝信息
     * @returns
     */
    async setClipboardData({ template, text }: { template?: IClipboardTemplate; text?: string }) {
        if (!template && !text) return;
        this.framePasteInfo = null;

        // 提供系统剪贴板的纯文本的写入逻辑，同时清空自定义剪贴板
        if (text) {
            writeClipboardData(text);
            this.pasteInfo = null;
            this.emitClipboardChange(null);
            return;
        }

        if (!template) return;

        const copyTemplate = cloneDeep(template);
        const isCopyPage = Array.isArray(copyTemplate);
        const flattenLayers = Array.isArray(copyTemplate)
            ? copyTemplate.map((page) => page.layer).flat()
            : [copyTemplate.layer];

        // 复制前的图层数据清理
        await asyncTraverseTrees(flattenLayers, async (item: ILayerProps) => {
            if (!isCopyPage) {
                removeAIPPTExtraInfo(item);
            }
            // 删除 ai 生成操作
            delete item.aiGcOperate;
            await this.framework.service.behavior.get(item)?.convertCodeToHttpUrl?.(item);
        });

        if (!Array.isArray(copyTemplate)) {
            this.setPasteInfo(copyTemplate);
        }

        writeClipboardData(copyTemplate, true);

        this.emitClipboardChange(template);
    }

    /**
     * 同步保存已裁剪的文本选区，系统纯文本由调用方的复制事件写入
     *
     * 文本选区不经过整图层复制的异步资源转换，避免剪切后或连续复制时延迟写入。
     *
     * @param text - 选中的纯文本
     * @param template - 已准备好的文本图层；null 时清除旧图层剪贴板
     */
    setTextSelectionClipboardData(text: string, template: IClipboardLayer | null): void {
        if (!text) return;
        const copied = cloneDeep(template);
        this.framePasteInfo = null;
        this.pasteInfo = null;
        if (copied) {
            removeAIPPTExtraInfo(copied.layer);
            this.setPasteInfo(copied);
        }
        writeClipboardData(copied ?? text, true, false);
        this.emitClipboardChange(copied);
    }

    private getMousePasteTarget() {
        const { edgelessCanvas, frame, transform } = this.framework.service;
        if (!edgelessCanvas.isEdgelessMode) return null;

        const point = transform.getScenePoint(getLastMousePoint());
        const frameId = frame.pick(point);

        return { frameId, point };
    }

    /** 普通模式为激活画布，无限画布下优先 hover frame / 鼠标所在 frame / frame selection / 上次实际粘贴 frame / 源 frame */
    private getPasteTargetInfo(copyLayer?: ILayerProps) {
        const { edgelessCanvas, frame } = this.framework.service;
        const destOriginIndex = frame.getActiveFrameId();
        if (edgelessCanvas.isEdgelessMode) {
            const sourceFrameId = copyLayer
                ? (this.framework.service.frame.getLayerFrameId(copyLayer.id) ?? copyLayer.__originIndex)
                : null;
            const selectedFrameId = frame.getFrameSelectionFrameId();
            const hoverFrameId = frame.getHoverFrameId();
            if (isNumber(hoverFrameId)) {
                return { frameId: hoverFrameId };
            }

            const mouseTarget = this.getMousePasteTarget();
            if (mouseTarget && isNumber(mouseTarget.frameId)) {
                return { frameId: mouseTarget.frameId };
            }

            if (isNumber(selectedFrameId)) {
                return { frameId: selectedFrameId };
            }

            if (this.pasteInfo && !this.checkPasteInfoReferenceChanged() && frame.get(this.pasteInfo.originIndex)) {
                return { frameId: this.pasteInfo.originIndex };
            }

            if (isNumber(sourceFrameId) && frame.get(sourceFrameId)) {
                return { frameId: sourceFrameId };
            }

            if (mouseTarget) {
                return {
                    frameId: destOriginIndex,
                    point: mouseTarget.point,
                };
            }
        }
        return { frameId: destOriginIndex };
    }

    async copyFrame(stageSelection: number[], e?: ClipboardEvent | null) {
        const { framework } = this;

        // ATTENTION: 判断来自系统事件的复制，通知业务侧用来埋点
        if (e) this.emitHotkeyEvent('copy', true);

        const clipboardPages: IClipboardPage[] = [];
        for (const index of stageSelection) {
            const isImageEditor = framework.service.frame.getMainLayer(index);
            // 图片编辑器暂不支持页面复制
            if (isImageEditor) continue;
            const layer = framework.service.frame.cloneFrame(index);
            clipboardPages.push({
                layer,
                extraInfo: framework.service.frame.getExtraInfo(index),
            });
        }

        await this.setClipboardData({ template: clipboardPages });
    }

    private async copyLayer(layer: ILayerProps, e?: ClipboardEvent | null) {
        const { framework } = this;
        const activeStageIndex = framework.service.frame.getActiveIndex();
        const activeExtraInfo = framework.service.frame.getExtraInfo(activeStageIndex);

        if (!layer) return;

        const tempLayer = cloneDeep(layer);
        const wrapLayer = framework.service.flatLayer.getWrapLayer(layer.id, true);

        // 拷贝剪切组内元素，需要校验花字素材id
        addAdvancedTextMaterialId(tempLayer, wrapLayer);

        // ATTENTION: 判断来自系统事件的复制，通知业务侧用来埋点
        if (e) this.emitHotkeyEvent('copy', false);

        const clipboardLayer: IClipboardLayer = {
            layer: tempLayer,
            extraInfo: activeExtraInfo,
        };

        // 记录粘贴的相对图层为源图层
        this.setPasteInfo(clipboardLayer);
        await this.setClipboardData({ template: clipboardLayer });
    }

    async copy(e?: ClipboardEvent | null) {
        const { framework } = this;
        const activeInfo = framework.service.frame.getActiveInfo()?.info;
        const stageSelection = framework.service.frame.getFrameSelection();

        // 复制整页
        if (!activeInfo && stageSelection.length) {
            await this.copyFrame(stageSelection, e);
            return;
        }

        if (
            activeInfo &&
            !activeInfo.lock &&
            !framework.service.behavior.get(activeInfo.layerType as LayerType)?.checkDisableCopy?.()
        ) {
            const layer = framework.service.flatLayer.getLayer(activeInfo.pickId || activeInfo.id);

            await this.copyLayer(layer, e);
            return;
        }
    }

    private async cutStage(stageSelection: number[]) {
        const { framework } = this;
        const clipboardPages: IClipboardPage[] = [];
        for (const index of stageSelection) {
            const isImageEditor = framework.service.frame.getMainLayer(index);
            // 图片编辑器暂不支持页面复制
            if (isImageEditor) continue;
            const layer = framework.service.frame.cloneFrame(index);
            clipboardPages.push({
                layer,
                extraInfo: framework.service.frame.getExtraInfo(index),
            });
        }

        const res = this.framework.service.registration
            .getInstance<ClipboardPluginDependency>('ClipboardPluginDependency')
            .deleteStages(stageSelection);

        if (!res) return;

        await this.setClipboardData({ template: clipboardPages });
    }

    private cutLayer(layer: ILayerProps, e?: ClipboardEvent | null) {
        if (!layer) return;

        if (!this.framework.service.flatLayer.checkPossibleCut(layer)) {
            return;
        }

        const { framework } = this;
        const activeStageIndex = framework.service.frame.getActiveIndex();
        const activeExtraInfo = framework.service.frame.getExtraInfo(activeStageIndex);
        const delLayerId = layer.id;
        const wrapLayer = framework.service.flatLayer.getWrapLayer(delLayerId, true);

        this.framework.service.registration
            .getInstance<ClipboardPluginDependency>('ClipboardPluginDependency')
            .deleteLayer(delLayerId);

        // 拷贝剪切组内元素，需要校验花字素材id
        addAdvancedTextMaterialId(layer, wrapLayer);

        // ATTENTION: 判断来自系统事件的剪切，通知业务侧用来埋点
        if (e) this.emitHotkeyEvent('cut', false);

        const clipboardLayer: IClipboardLayer = {
            layer,
            extraInfo: activeExtraInfo,
        };

        // 记录粘贴的相对图层为源图层
        this.setPasteInfo(clipboardLayer);
        this.setClipboardData({ template: clipboardLayer });
    }

    async cut(e?: ClipboardEvent | null) {
        const { framework } = this;
        const activeInfo = framework.service.frame.getActiveInfo()?.info;
        const stageSelection = framework.service.frame.getFrameSelection();

        // 剪切页面
        if (!activeInfo && stageSelection.length) {
            await this.cutStage(stageSelection);
            return;
        }

        if (
            activeInfo &&
            !activeInfo.lock &&
            !framework.service.behavior.get(activeInfo.layerType as LayerType)?.checkDisableCut?.()
        ) {
            const delLayerId = activeInfo.pickId || activeInfo.id;
            const delLayer = framework.service.flatLayer.getLayer(delLayerId);

            this.cutLayer(delLayer, e);
            return;
        }
    }

    private getPastedPoint(
        copyLayer: ILayerProps,
        copyExtraInfo: IExtraInfo,
        destFrameId: number,
        copyCenterPoint?: IPoint,
        isUseOriginalPosition?: boolean,
    ) {
        const { framework } = this;
        const targetExtraInfo = framework.service.frame.getExtraInfoByFrameId(destFrameId);

        // 指定位置粘贴（右键菜单），优先级最高
        if (copyCenterPoint) {
            return copyCenterPoint;
        }

        const sourcePoint = {
            x: copyLayer.left,
            y: copyLayer.top,
        };

        // 强制按原位置粘贴（Shift+Ctrl+V）
        if (isUseOriginalPosition) {
            return sourcePoint;
        }

        // 无限画布模式
        if (framework.service.edgelessCanvas.isEdgelessMode) {
            return this.getEdgelessPastedPoint(sourcePoint, destFrameId);
        }

        let tw = targetExtraInfo.width;
        let th = targetExtraInfo.height;
        if (!tw || !th) {
            // 长画布 / 单画布等 extraInfo 无固定尺寸的场景，改用 frame.getSize 获取画布实际尺寸
            const fallbackSize = framework.service.frame.getSize(destFrameId);
            tw = fallbackSize.width;
            th = fallbackSize.height;
        }

        if (!tw || !th) {
            // 仍然无法获取尺寸，降级处理
            const isCrossFrame = this.pasteInfo && destFrameId !== this.pasteInfo.originIndex;
            if (isCrossFrame) {
                return sourcePoint;
            }
            return this.getOffsetPastedPoint(sourcePoint);
        }

        const targetStageSize: ISize = { width: tw, height: th };
        const isCrossFrame = this.pasteInfo && destFrameId !== this.pasteInfo.originIndex;

        if (isCrossFrame) {
            // ── 跨画布首次粘贴 ──
            const sourceExtra = this.pasteInfo!.extraInfo ?? copyExtraInfo;
            let sourceWidth = sourceExtra?.width;
            let sourceHeight = sourceExtra?.height;
            if (!sourceWidth || !sourceHeight) {
                const sourceFallback = framework.service.frame.getSize(this.pasteInfo!.originIndex);
                sourceWidth = sourceFallback.width;
                sourceHeight = sourceFallback.height;
            }
            const isSameSize = sourceWidth === tw && sourceHeight === th;

            if (isSameSize) {
                // 尺寸一致：原位置，超出画布则居中
                return makeSurePointInStage(sourcePoint, copyLayer, targetStageSize);
            }
            // 尺寸不一致：直接居中
            return {
                x: tw / 2 - copyLayer.width / 2,
                y: th / 2 - copyLayer.height / 2,
            };
        }

        // ── 同画布粘贴（含首次、第二次及以后） ──
        const offsetPoint = this.getOffsetPastedPoint(sourcePoint);

        return makeSurePointInStage(offsetPoint, copyLayer, targetStageSize);
    }

    /** 无限画布模式下的偏移计算 */
    private getEdgelessPastedPoint(sourcePoint: IPoint, destFrameId: number) {
        if (this.pasteInfo && this.pasteInfo.originIndex !== destFrameId) {
            return sourcePoint;
        }

        return this.getOffsetPastedPoint(sourcePoint);
    }

    private checkPasteInfoReferenceChanged() {
        const { framework } = this;
        if (!this.pasteInfo) return false;

        const frameOffset = this.getPasteInfoFrameOffset(this.pasteInfo.originIndex) ?? { left: 0, top: 0 };
        const deletedLayer = this.pasteInfo.layers.find((layer) => !framework.service.flatLayer.getLayer(layer.id));
        if (deletedLayer) return true;

        return this.pasteInfo.layers.some((layer) => {
            const cur = framework.service.flatLayer.getLayer(layer.id);
            if (!cur) return true;
            return cur.left !== layer.left + frameOffset.left || cur.top !== layer.top + frameOffset.top;
        });
    }

    private getPasteInfoFrameOffset(destFrameId: number) {
        if (!this.pasteInfo) return null;

        // 非无限画布下图层存的是页内坐标，而 frame shape 的 top 含长图模式的 offsetTop，不能当作平移量
        if (!this.framework.service.edgelessCanvas.isEdgelessMode) return { left: 0, top: 0 };

        const targetFrameShape = this.framework.service.frame.getShape(destFrameId);
        if (!targetFrameShape) return null;

        return {
            left: targetFrameShape.left - this.pasteInfo.frame.left,
            top: targetFrameShape.top - this.pasteInfo.frame.top,
        };
    }

    /**
     * 同画布连续粘贴偏移：
     * - 首次（无 pasteInfo 或相对图层已删/已移）→ 原位置
     * - 二次及以后 → 上一次位置 + COPY_DISTANCE
     */
    private getOffsetPastedPoint(sourcePoint: IPoint) {
        if (!this.pasteInfo || this.checkPasteInfoReferenceChanged()) {
            return sourcePoint;
        }

        const frameOffset = this.getPasteInfoFrameOffset(this.pasteInfo.originIndex) ?? { left: 0, top: 0 };
        return {
            x: this.pasteInfo.left + frameOffset.left + COPY_DISTANCE,
            y: this.pasteInfo.top + frameOffset.top + COPY_DISTANCE,
        };
    }

    /**
     * 粘贴图层，并激活
     * @param copyLayer - 拷贝的图层
     * @param copyCenterPoint - 拷贝图层的中心点
     * @param copyExtraInfo - 拷贝图层的extraInfo
     * @param isUseOriginalPosition - 是否使用原位置粘贴
     * @returns
     */
    private pasteLayer(
        copyLayer: ILayerProps,
        copyExtraInfo: IExtraInfo,
        {
            point: copyCenterPoint,
            isUseOriginalPosition,
        }: {
            point?: IPoint;
            isUseOriginalPosition?: boolean;
        },
    ) {
        // 容错处理
        if (!copyLayer || copyLayer.lock || copyLayer.parentId) return;

        const { identity, frame: frameOperate } = this.framework.service;

        const pasteTarget = this.getPasteTargetInfo(copyLayer);
        const destOriginIndex = pasteTarget.frameId;
        const isPasteInfoReferenceChanged = this.checkPasteInfoReferenceChanged();

        // 原图层可能已删除，优先使用剪贴板中保留的源 frame 维持跨 frame 平移。
        const sourceFrameId =
            frameOperate.getLayerFrameId(copyLayer.id) ?? copyLayer.__originIndex ?? this.pasteInfo?.originIndex;
        const targetExtraInfo = frameOperate.getExtraInfoByFrameId(destOriginIndex);

        // 从付费模板粘贴素材到免费模板，需将免费模板变成付费模板
        const needSetVip = !targetExtraInfo.threshold && !!copyExtraInfo?.threshold;
        const newExtraInfoInfo = needSetVip ? { ...targetExtraInfo, threshold: 1 } : { ...targetExtraInfo };
        const isSamePasteTarget = this.pasteInfo?.originIndex === destOriginIndex;
        const targetPoint = !isSamePasteTarget ? pasteTarget.point : undefined;

        const pastedPoint = this.getPastedPoint(
            copyLayer,
            copyExtraInfo,
            destOriginIndex,
            copyCenterPoint ?? targetPoint,
            targetPoint ? false : isUseOriginalPosition,
        );
        const newInfo = cloneDeep(copyLayer);

        // frameOffset 仅在首次跨 frame / 源 frame 整体移动后粘贴时使用，连续粘贴走 pastedPoint 偏移
        const isCrossFrame = sourceFrameId !== destOriginIndex;
        const pasteInfoFrameOffset =
            this.pasteInfo?.originIndex === sourceFrameId ? this.getPasteInfoFrameOffset(destOriginIndex) : null;
        const isSourceFrameMoved =
            !isCrossFrame &&
            isPasteInfoReferenceChanged &&
            Boolean(pasteInfoFrameOffset?.left || pasteInfoFrameOffset?.top);
        const shouldUseFrameOffset =
            this.framework.service.edgelessCanvas.isEdgelessMode &&
            !targetPoint &&
            isNumber(sourceFrameId) &&
            isNumber(destOriginIndex) &&
            (isSourceFrameMoved ||
                (isCrossFrame &&
                    (!this.pasteInfo ||
                        isPasteInfoReferenceChanged ||
                        this.pasteInfo.originIndex !== destOriginIndex)));

        let frameOffset: IPosition | undefined = undefined;
        if (shouldUseFrameOffset) {
            const sourceFrameInfo = frameOperate.get(sourceFrameId);
            const targetFrameInfo = frameOperate.get(destOriginIndex);
            if (pasteInfoFrameOffset) {
                frameOffset = pasteInfoFrameOffset;
            } else if (sourceFrameInfo && targetFrameInfo) {
                frameOffset = {
                    left: targetFrameInfo.left - sourceFrameInfo.left,
                    top: targetFrameInfo.top - sourceFrameInfo.top,
                };
            } else if (targetFrameInfo) {
                const centerPoint = {
                    x: targetFrameInfo.width / 2 + targetFrameInfo.left,
                    y: targetFrameInfo.height / 2 + targetFrameInfo.top,
                };
                frameOffset = {
                    left: centerPoint.x - newInfo.width / 2 - newInfo.left,
                    top: centerPoint.y - newInfo.height / 2 - newInfo.top,
                };
            }
        }

        if (frameOffset) {
            resetLayer(newInfo, frameOffset.left, frameOffset.top);
        } else {
            resetLayer(newInfo, pastedPoint.x - copyLayer.left, pastedPoint.y - copyLayer.top);
        }

        traverseTree(newInfo, (layer: ILayerProps) => {
            removeAIPPTExtraInfo(layer);
        });

        this.framework.service.registration
            .getInstance<ClipboardPluginDependency>('ClipboardPluginDependency')
            .addLayer(newInfo, {
                isActiveNewLayer: true,
                isWriteHistory: true,
                checkFocus: true,
                trackFunc: getTrackFunc(identity, copyLayer, 'copy'),
                destOriginIndex,
                extraInfo: {
                    oldExtraInfo: targetExtraInfo,
                    newExtraInfo: newExtraInfoInfo,
                },
            });

        // 粘贴后，相对图层改为新粘贴的图层，显式传入目标画布索引
        this.setPasteInfo(
            {
                layer: newInfo,
                extraInfo: newExtraInfoInfo,
            },
            destOriginIndex,
        );
        return true;
    }

    private getStageBaseInfo(stage: ILayerProps[]): IPosition & ISize {
        const backgroundLayer = stage.find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER);
        if (backgroundLayer) {
            return {
                left: backgroundLayer.left,
                top: backgroundLayer.top,
                width: backgroundLayer.width,
                height: backgroundLayer.height,
            };
        }

        let minLeft = Number.POSITIVE_INFINITY;
        let minTop = Number.POSITIVE_INFINITY;
        let maxRight = Number.NEGATIVE_INFINITY;
        let maxBottom = Number.NEGATIVE_INFINITY;
        stage.forEach((rootLayer) => {
            traverseTree(rootLayer, (layer: ILayerProps) => {
                minLeft = Math.min(minLeft, layer.left);
                minTop = Math.min(minTop, layer.top);
                maxRight = Math.max(maxRight, layer.left + layer.width);
                maxBottom = Math.max(maxBottom, layer.top + layer.height);
            });
        });

        if (!Number.isFinite(minLeft) || !Number.isFinite(minTop)) {
            return { left: 0, top: 0, width: 0, height: 0 };
        }

        return {
            left: minLeft,
            top: minTop,
            width: maxRight - minLeft,
            height: maxBottom - minTop,
        };
    }

    private translateStage(stage: ILayerProps[], offset: IPosition) {
        stage.forEach((rootLayer) => {
            traverseTree(rootLayer, (layer: ILayerProps) => {
                layer.left += offset.left;
                layer.top += offset.top;
            });
        });
    }

    private cloneStageForPaste(stage: ILayerProps[]) {
        const clonedStage = cloneDeep(stage);
        clonedStage.forEach((rootLayer) => {
            traverseTree(rootLayer, (layer: ILayerProps) => {
                layer.id = uuid();
                layer.children?.forEach((child) => {
                    child.parentId = layer.id;
                });
            });
        });

        return clonedStage;
    }

    private alignNormalFramesToTarget(stages: ILayerProps[][], extraInfos: IExtraInfo[], targetStageIndexes: number[]) {
        const { edgelessCanvas, frame } = this.framework.service;
        if (!edgelessCanvas.isEdgelessMode) return;

        let nextLeft = this.framePasteInfo?.nextLeft;
        let top = this.framePasteInfo?.top;
        if (targetStageIndexes.length) {
            const targetFrameId = frame.indexToFrameId(Math.max.apply(null, targetStageIndexes));
            const targetFrameShape = frame.getShape(targetFrameId);
            nextLeft = targetFrameShape.left + targetFrameShape.width + FRAME_PASTE_GAP;
            top = targetFrameShape.top;
        }
        if (!isNumber(nextLeft) || !isNumber(top)) return;

        let currentNextLeft = nextLeft;
        const currentTop = top;
        stages.forEach((stage, index) => {
            if (extraInfos[index]?.isPPT) return;

            const sourceInfo = this.getStageBaseInfo(stage);
            this.translateStage(stage, {
                left: currentNextLeft - sourceInfo.left,
                top: currentTop - sourceInfo.top,
            });
            currentNextLeft += sourceInfo.width + FRAME_PASTE_GAP;
        });
        this.framePasteInfo = { nextLeft: currentNextLeft, top: currentTop };
    }

    private getFramePasteInsertIndex(extraInfos: IExtraInfo[], targetStageIndexes: number[]) {
        const { edgelessCanvas, edgelessPPT, frame } = this.framework.service;
        if (!edgelessCanvas.isEdgelessMode) {
            return targetStageIndexes.length ? Math.max.apply(null, targetStageIndexes) : undefined;
        }

        const isPastePPTFrames = extraInfos.length > 0 && extraInfos.every((extraInfo) => extraInfo?.isPPT);
        if (isPastePPTFrames) {
            const defaultTaskId = this.framework.service.agentLayout.getEnable()
                ? this.framework.service.agentLayout.getDefaultPPTTaskId()
                : undefined;
            const taskId = edgelessPPT.getPPTExtraInfoGroupTaskId(extraInfos[0], defaultTaskId);
            const pptFrameIds = edgelessPPT.getPPTFrameIdsByTaskId(taskId, defaultTaskId);
            const lastPPTFrameId = pptFrameIds[pptFrameIds.length - 1];
            return isNumber(lastPPTFrameId) ? frame.frameIdToIndex(lastPPTFrameId) : undefined;
        }

        return targetStageIndexes.length ? Math.max.apply(null, targetStageIndexes) : undefined;
    }

    private getPasteableStagePages(pagesPasted: IClipboardPage[]) {
        const { edgelessCanvas, edgelessPPT, frame } = this.framework.service;

        if (!edgelessCanvas.isEdgelessMode) {
            const remainCount = MAX_STAGES - frame.getFrameIdsLength();
            return pagesPasted.slice(0, Math.max(remainCount, 0));
        }

        const defaultTaskId = this.framework.service.agentLayout.getEnable()
            ? this.framework.service.agentLayout.getDefaultPPTTaskId()
            : undefined;
        const remainPPTCountMap = new Map<string, number>();

        return pagesPasted.filter((page) => {
            if (!page.extraInfo?.isPPT) return true;

            const taskId = edgelessPPT.getPPTExtraInfoGroupTaskId(page.extraInfo, defaultTaskId);
            const taskKey = taskId || '';
            const remainPPTCount =
                remainPPTCountMap.get(taskKey) ?? edgelessPPT.getRemainingPPTFrameCount(taskId, defaultTaskId);
            if (remainPPTCount <= 0) return false;

            remainPPTCountMap.set(taskKey, remainPPTCount - 1);
            return true;
        });
    }

    private pasteStage(
        pagesPasted: IClipboardPage[],
        targetStageIndexes = this.framework.service.frame.getFrameSelection(),
    ) {
        const { framework } = this;
        const originIndex = framework.service.frame.getActiveFrameId();
        const isImageEditor = framework.service.frame.getMainLayer(originIndex);
        // 图片编辑器暂不支持页面粘贴
        if (isImageEditor) return;

        const pasteablePages = this.getPasteableStagePages(pagesPasted);
        if (pasteablePages.length < pagesPasted.length) {
            Message.warning(MAX_STAGES_TIP());
        }
        if (!pasteablePages.length) {
            return;
        }

        const stages: ILayerProps[][] = pasteablePages.map((page) => this.cloneStageForPaste(page.layer));
        const extraInfos: IExtraInfo[] = pasteablePages.map((page) => cloneDeep(page.extraInfo));
        const activeStageIndex = framework.service.frame.getActiveIndex();
        const activeExtraInfo = framework.service.frame.getExtraInfo(activeStageIndex);
        const isLongImageMode = framework.service.edgelessCanvas.isLongImageMode;
        const insertIndex = this.getFramePasteInsertIndex(extraInfos, targetStageIndexes);
        const hasNormalFrames = extraInfos.some((extraInfo) => !extraInfo?.isPPT);

        stages.forEach((stage, index) => {
            const currentExtraInfo = extraInfos[index];
            if (
                isLongImageMode &&
                activeExtraInfo.width &&
                activeExtraInfo.height &&
                currentExtraInfo.width &&
                currentExtraInfo.height &&
                currentExtraInfo.width !== activeExtraInfo.width
            ) {
                const oldSize = {
                    width: currentExtraInfo.width,
                    height: currentExtraInfo.height,
                };
                const ratio = currentExtraInfo.width / currentExtraInfo.height;

                currentExtraInfo.width = activeExtraInfo?.width;
                currentExtraInfo.height = currentExtraInfo.width / ratio;

                this.framework.service.frame.scaleLayers(stage, oldSize, {
                    width: currentExtraInfo.width,
                    height: currentExtraInfo.height,
                });
            }
        });

        if (hasNormalFrames) {
            this.alignNormalFramesToTarget(stages, extraInfos, targetStageIndexes);
        } else {
            this.framePasteInfo = null;
        }
        // 粘贴到多画布舞台时需要进行缩放处理
        this.framework.service.registration
            .getInstance<ClipboardPluginDependency>('ClipboardPluginDependency')
            .addStages(stages, extraInfos, true, false, insertIndex);
    }

    pasteFrame(e?: ClipboardEvent | null, targetStageIndexes?: number[]) {
        if (this.framework.service.editor.disableMouseSelect) return false;

        const clipboardData = readClipboardData(e);
        if (!clipboardData.template) return false;

        if (Array.isArray(clipboardData.template)) {
            this.pasteStage(clipboardData.template as IClipboardPage[], targetStageIndexes);
            this.framework.service.frame.clearFrameSelection();
            this.framework.service.frame.setHoverFrameId(null);
            if (e) this.emitHotkeyEvent('paste', true);
            return true;
        }

        const layerPasted = clipboardData.template as IClipboardLayer;
        this.pasteLayer(layerPasted.layer, layerPasted.extraInfo, {
            isUseOriginalPosition: !e?.clipboardData,
        });
        this.framework.service.frame.clearFrameSelection();
        this.framework.service.frame.setHoverFrameId(null);
        if (e) this.emitHotkeyEvent('paste', false);
        return true;
    }

    paste(e?: ClipboardEvent | null, point?: IPoint, isUseOriginalPosition?: boolean) {
        if (this.framework.service.editor.disableMouseSelect) return;

        const clipboardData = readClipboardData(e);

        // 粘贴的带/t和/n就按表格处理
        const clipboardTextIsTableInfo =
            (clipboardData.text.includes('\t') && clipboardData.text.includes('\n') && !clipboardData.html) ||
            clipboardData.html.includes('<table');

        // 先处理表格再处理 file (wps excel粘贴出来的既有file又有文本)
        // FIXME: 第二判断条件先临时兼容无限画布下没有 TableLayer 实例的场景
        if (clipboardTextIsTableInfo && this.framework.service.registration.getInstance<TableLayer>('TableLayer')) {
            this.framework.service.mask.show({ loading: true });
            this.framework.service.registration
                .getInstance<ClipboardPluginDependency>('ClipboardPluginDependency')
                .addTable(clipboardData)
                .finally(() => {
                    this.framework.service.mask.hide();
                });
            return;
        }

        if (clipboardData.files.length) {
            // 文件粘贴上传
            this.emitPasteFile(clipboardData.files);
            return;
        }

        let layerPasted: IClipboardLayer | undefined;
        let pagesPasted: IClipboardPage[] | undefined;

        if (clipboardData.template) {
            // 判断是否为多舞台数据格式
            const isMultiStageFormat = Array.isArray(clipboardData.template);

            if (isMultiStageFormat) {
                pagesPasted = clipboardData.template as IClipboardPage[];
            } else {
                layerPasted = clipboardData.template as IClipboardLayer;
            }
        }

        if (isUseSystemClipboard(clipboardData)) {
            // 业务侧注册处理器时接管文字粘贴，否则沿用默认文字图层创建逻辑
            if (this.pasteTextHandles.size > 0) {
                this.emitPasteText(clipboardData.text);
                // ATTENTION: 判断来自系统事件的粘贴，通知业务侧用来埋点
                if (e) this.emitHotkeyEvent('paste', false);
                return;
            }
            this.framework.service.registration
                .getInstance<ClipboardPluginDependency>('ClipboardPluginDependency')
                .addText(clipboardData);
            // ATTENTION: 判断来自系统事件的粘贴，通知业务侧用来埋点
            if (e) this.emitHotkeyEvent('paste', false);
            return;
        }

        // 页面粘贴
        if (pagesPasted) {
            this.pasteStage(pagesPasted);
            // ATTENTION: 判断来自系统事件的粘贴，通知业务侧用来埋点
            if (e) this.emitHotkeyEvent('paste', true);
            return;
        }

        if (!layerPasted) return;

        // HACK: 没有 clipboardData 的粘贴事件，来自触发原位置粘贴事件
        this.pasteLayer(layerPasted.layer, layerPasted.extraInfo, {
            point,
            isUseOriginalPosition: isUseOriginalPosition ?? !e?.clipboardData,
        });
        // ATTENTION: 判断来自系统事件的粘贴，通知业务侧用来埋点
        if (e) this.emitHotkeyEvent('paste', false);
    }

    private static pasteFileEventName = 'plugin-clipboard-paste-file';

    private emitPasteFile(files: File[]) {
        this.framework.service.events.emit(ClipboardPlugin.pasteFileEventName, files);
    }

    onPasteFile(fn: (files: File[]) => void) {
        const handle = this.framework.service.events.on(ClipboardPlugin.pasteFileEventName, fn);

        return () => {
            this.framework.service.events.off(handle);
        };
    }

    private static pasteTextEventName = 'plugin-clipboard-paste-text';

    private emitPasteText(text: string) {
        this.framework.service.events.emit(ClipboardPlugin.pasteTextEventName, text);
    }

    /**
     * 注册业务侧文字粘贴处理器，接管默认文字图层创建；全部取消后恢复默认行为
     *
     * @param fn - 接收系统剪贴板文字并执行粘贴的回调
     * @returns 取消当前处理器的函数
     */
    onPasteText(fn: (text: string) => void) {
        const handle = this.framework.service.events.on(ClipboardPlugin.pasteTextEventName, fn);
        this.pasteTextHandles.add(handle);

        return () => {
            this.framework.service.events.off(handle);
            this.pasteTextHandles.delete(handle);
        };
    }

    private static clipboardChangeEventName = 'plugin-clipboard-change';

    private emitClipboardChange(template: IClipboardTemplate) {
        this.framework.service.events.emit(ClipboardPlugin.clipboardChangeEventName, { template });
    }

    /** ATTENTION: 当前因为还未做系统剪贴板的权限申请机制，因此只有自定义剪贴板变化才有触发事件 */
    onClipboardChange(fn: (clipboardData: IClipboardData) => void) {
        const handle = this.framework.service.events.on(ClipboardPlugin.clipboardChangeEventName, fn);

        return () => {
            this.framework.service.events.off(handle);
        };
    }

    private static hotkeyEventName = 'plugin-clipboard-hotkey-event';

    private emitHotkeyEvent(keyType: IClipboardHotkeyType, isPage: boolean) {
        this.framework.service.events.emit(ClipboardPlugin.hotkeyEventName, { keyType, isPage });
    }

    onHotkeyEvent(fn: (keyType: IClipboardHotkeyType, isPage: boolean) => void) {
        const handle = this.framework.service.events.on(ClipboardPlugin.hotkeyEventName, ({ keyType, isPage }) => {
            fn(keyType, isPage);
        });

        return () => {
            this.framework.service.events.off(handle);
        };
    }
}
